export type Verification = {
  challengeId: string;
  method: 'email_otp' | 'totp';
  destinationMasked: string | null;
  codeLength: number;
  expiresAt: string;
  resendAvailableAt: string | null;
  serverTime: string;
};
export type VerificationContext = { identifier: string; verification: Verification; clockOffset: number };
const memory = new Map<string, string>();
const key = (flow: string) => `identity-verification:${flow}`;
export function parseVerification(value: unknown): Verification {
  const v = value as Verification;
  if (!v || typeof v.challengeId !== 'string' || !v.challengeId.trim() ||
      !['email_otp', 'totp'].includes(v.method) ||
      (v.destinationMasked !== null && typeof v.destinationMasked !== 'string') ||
      !Number.isInteger(v.codeLength) || v.codeLength < 4 || v.codeLength > 10 ||
      ![v.expiresAt, v.serverTime].every(t => typeof t === 'string' && Number.isFinite(Date.parse(t))) ||
      (v.method !== 'totp' && (typeof v.resendAvailableAt !== 'string' || !Number.isFinite(Date.parse(v.resendAvailableAt))))) {
    throw new Error('Verification context is unavailable. Please sign in again.');
  }
  return { challengeId: v.challengeId, method: v.method, destinationMasked: v.destinationMasked,
    codeLength: v.codeLength, expiresAt: v.expiresAt, resendAvailableAt: v.resendAvailableAt, serverTime: v.serverTime };
}
export function saveVerification(flow: string, identifier: string, value: unknown): VerificationContext {
  const verification = parseVerification(value);
  const context = { identifier, verification, clockOffset: Date.parse(verification.serverTime) - Date.now() };
  memory.set(key(flow), JSON.stringify(context));
  try { sessionStorage.setItem(key(flow), JSON.stringify(context)); } catch { /* In-memory flow still works. */ }
  return context;
}
export function clearVerification(flow: string) {
  memory.delete(key(flow));
  try { sessionStorage.removeItem(key(flow)); } catch { /* Storage may be disabled. */ }
}
export function restoreVerification(flow: string): VerificationContext | null {
  try {
    let raw: string | null | undefined;
    try { raw = sessionStorage.getItem(key(flow)); } catch { raw = memory.get(key(flow)); }
    const c = JSON.parse(raw || 'null');
    if (!c || typeof c.identifier !== 'string' || !c.identifier || !Number.isFinite(c.clockOffset)) return null;
    const verification = parseVerification(c.verification);
    // Retain an expired code context so the user can explicitly resend it.
    const context = { identifier: c.identifier, verification, clockOffset: c.clockOffset };
    if (flow === 'mfa' && verificationTiming(context).expired) { clearVerification(flow); return null; }
    return context;
  } catch { clearVerification(flow); return null; }
}
export function verificationTiming(context: VerificationContext, now = Date.now()) {
  const serverNow = now + context.clockOffset;
  return { expired: serverNow >= Date.parse(context.verification.expiresAt),
    resendSeconds: Math.max(0, Math.ceil((Date.parse(context.verification.resendAvailableAt ?? context.verification.expiresAt) - serverNow) / 1000)) };
}

export function savePendingEmail(identifier: string) {
  clearVerification('email');
  memory.set(key('pending-email'), JSON.stringify({ identifier }));
  try { sessionStorage.setItem(key('pending-email'), JSON.stringify({ identifier })); } catch { /* Storage may be disabled. */ }
}
export function restorePendingEmail(): string {
  try { let raw: string | null | undefined;
    try { raw = sessionStorage.getItem(key('pending-email')); } catch { raw = memory.get(key('pending-email')); }
    const value = JSON.parse(raw || 'null'); return typeof value?.identifier === 'string' ? value.identifier : ''; } catch { return ''; }
}
